import type { ComponentProps } from '../blocks';
import { CHART_COLORS, CHART_FONT, resolveCssColor, type ChartColors } from './colors';
import { xmlEscape } from './escape';

/**
 * Default segment colors as categorical CSS tokens, assigned in fixed order (the
 * six-hue palette in `categorical.ts`). Split/pie slices are nominal categories,
 * so each takes the next slot's hue; the tokens are theme-aware on web and the
 * rasterizer maps them to light hex via `resolveCssColor`.
 */
export const SPLIT_PALETTE = ['--he-cat-1', '--he-cat-2', '--he-cat-3', '--he-cat-4', '--he-cat-5', '--he-cat-6'] as const;

/**
 * Default color for segment `i` when it has none: the categorical slot at `i`,
 * in fixed order. A 7th+ segment folds to the neutral "Other" swatch
 * (`--he-cat-other`) rather than cycling or generating a hue.
 */
export function splitColor(color: string | undefined, i: number): string {
  if (color) return color;
  if (i < SPLIT_PALETTE.length) return `var(${SPLIT_PALETTE[i]})`;
  return 'var(--he-cat-other)';
}

/** One normalized Split segment with its share of the total. */
export interface SplitPiece {
  label: string;
  value: number;
  color?: string;
  /** Share of the total in [0, 1]. */
  fraction: number;
  /** Percentage rounded to one decimal. */
  pct: number;
}

/**
 * Normalize Split segments into pieces carrying each one's share of the total.
 * Negative values clamp to 0; an all-zero total yields zero fractions (caller
 * renders an empty track).
 */
export function splitPieces(segments: ComponentProps['Split']['segments']): { pieces: SplitPiece[]; total: number } {
  const vals = segments.map((s) => Math.max(0, s.value));
  const total = vals.reduce((a, b) => a + b, 0);
  const pieces = segments.map((s, i) => {
    const fraction = total > 0 ? vals[i] / total : 0;
    return { label: s.label, value: s.value, color: s.color, fraction, pct: Math.round(fraction * 1000) / 10 };
  });
  return { pieces, total };
}

/** Layout constants for the rasterized split bar. */
export const SPLIT_DIMS = { W: 720, barH: 26, legendLineH: 24 } as const;

/**
 * Standalone stacked-bar SVG for rasterization (Slack PNGs). Mirrors the React
 * `SplitBar` primitive: one rounded bar of proportional segments + a legend.
 */
export function splitSvg(props: ComponentProps['Split'], colors: ChartColors = CHART_COLORS): string {
  const { title, segments, unit = '' } = props;
  const { W, barH, legendLineH } = SPLIT_DIMS;
  const titleH = title ? 34 : 8;
  const barY = titleH;
  const legendY = barY + barH + 16;
  const totalH = legendY + segments.length * legendLineH;
  const { pieces, total } = splitPieces(segments);
  const unitSuffix = unit ? ` ${unit}` : '';

  let cx = 0;
  const bars = pieces
    .map((p, i) => {
      const w = p.fraction * W;
      const rect = `<rect x="${cx.toFixed(1)}" y="${barY}" width="${w.toFixed(1)}" height="${barH}" fill="${xmlEscape(resolveCssColor(splitColor(p.color, i), colors))}"/>`;
      cx += w;
      return rect;
    })
    .join('');

  const legend = pieces
    .map((p, i) => {
      const y = legendY + i * legendLineH;
      const swatch = `<rect x="0" y="${y - 9}" width="11" height="11" rx="3" fill="${xmlEscape(resolveCssColor(splitColor(p.color, i), colors))}"/>`;
      const text = `${p.label}  ${total > 0 ? `${p.pct}%` : '—'}  ${p.value.toLocaleString()}${unitSuffix}`;
      return swatch + `<text x="18" y="${y}" font-size="13" fill="${colors.text}">${xmlEscape(text)}</text>`;
    })
    .join('');

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${totalH}" width="${W}" height="${totalH}" font-family='${CHART_FONT}'>` +
    `<rect width="${W}" height="${totalH}" fill="${colors.bg}"/>` +
    (title ? `<text x="0" y="22" font-size="15" font-weight="600" fill="${colors.text}">${xmlEscape(title)}</text>` : '') +
    `<defs><clipPath id="splitClip"><rect x="0" y="${barY}" width="${W}" height="${barH}" rx="8"/></clipPath></defs>` +
    `<g clip-path="url(#splitClip)">` +
    `<rect x="0" y="${barY}" width="${W}" height="${barH}" fill="${colors.track}"/>` +
    bars +
    `</g>` +
    legend +
    `</svg>`
  );
}
